/**
 * Admin reconciliation API client — F-009.5 (trigger manuale).
 *
 *  - POST /api/v1/admin/wallet/reconciliation/run → ReconciliationReport
 *
 * Mirror di `features/admin-listino/lib/listino-client.ts`:
 * `credentials: 'include'`, errori → `ReconciliationApiError` (status+code),
 * `errorMessageFor(err)` per i toast.
 */
const API = '/api/v1/admin/wallet/reconciliation/run';

/** Esito di una run — speculare a `ReconciliationReport` lato API. */
export interface ReconciliationReport {
  windowFromUnix: number;
  windowFromIso: string;
  scanned: number;
  matched: number;
  recovered: number;
  skipped: number;
  failed: number;
  recoveredSessionIds: string[];
}

export class ReconciliationApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ReconciliationApiError';
    this.status = status;
    this.code = code;
  }
}

export function isReconciliationApiError(err: unknown): err is ReconciliationApiError {
  return err instanceof ReconciliationApiError;
}

const ERROR_MESSAGES: Record<string, string> = {
  STRIPE_NOT_CONFIGURED: 'Stripe non è configurato su questo ambiente.',
  STRIPE_CHECKOUT_FAILED: 'Errore nel contattare Stripe. Riprova più tardi.',
  VALIDATION_ERROR: 'Parametri non validi.',
  FORBIDDEN: 'Accesso negato.',
  UNAUTHORIZED: "Sessione scaduta. Effettua di nuovo l'accesso.",
};

export function errorMessageFor(err: unknown): string {
  if (isReconciliationApiError(err)) {
    return ERROR_MESSAGES[err.code] ?? err.message ?? 'Si è verificato un errore.';
  }
  if (err instanceof TypeError) return 'Errore di connessione. Riprova.';
  return 'Si è verificato un errore. Riprova.';
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function throwForError(
  res: Response,
  fallbackCode: string,
  fallbackMsg: string,
): Promise<never> {
  const env = (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
  throw new ReconciliationApiError(res.status, env.code ?? fallbackCode, env.message ?? fallbackMsg);
}

export async function runReconciliation(lookbackHours?: number): Promise<ReconciliationReport> {
  const res = await fetch(API, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(lookbackHours != null ? { lookbackHours } : {}),
  });
  if (!res.ok) await throwForError(res, 'RUN_FAILED', 'Impossibile eseguire la riconciliazione');
  return (await res.json()) as ReconciliationReport;
}
